// Thin fetch wrapper for the GabSync endpoints. Matches the portal convention: native fetch, no
// axios, the Okta bearer token passed per-call, gateway route ${GATEWAY_BASE_URL}/multitenancy/api.

import {
  CustomFieldError,
  GabCompareDefaults,
  GabSyncRunDetail,
  GabSyncRunSummary,
  RowResolution,
} from "./types";

const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;
const BASE = `${GATEWAY_BASE_URL}/multitenancy/api/GabSync`;

function authHeaders(token: string, withBody: boolean): HeadersInit {
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (withBody) {
    headers["Content-Type"] = "application/json";
    headers["Accept"] = "application/json";
  }
  return headers;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // Surface the server's message (400/404/409 carry a plain-text reason) so the UI can show it.
    const text = await res.text().catch(() => "");
    throw new Error(text || `Request failed (${res.status})`);
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  return (await res.json()) as T;
}

export function fetchRunsForProduct(
  token: string,
  productGuid: string
): Promise<GabSyncRunSummary[]> {
  return fetch(`${BASE}/products/${productGuid}/runs`, {
    headers: authHeaders(token, false),
  }).then((r) => handle<GabSyncRunSummary[]>(r));
}

export function fetchRuns(token: string): Promise<GabSyncRunSummary[]> {
  return fetch(`${BASE}/runs`, { headers: authHeaders(token, false) }).then((r) =>
    handle<GabSyncRunSummary[]>(r)
  );
}

export function applyRun(
  token: string,
  runId: string,
  confirmation: string
): Promise<GabSyncRunDetail> {
  return fetch(`${BASE}/runs/${runId}/apply`, {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify({ confirmation }),
  }).then((r) => handle<GabSyncRunDetail>(r));
}

// Returns [] when all custom edits are valid, otherwise the per-field errors.
export function validateCustom(token: string, runId: string): Promise<CustomFieldError[]> {
  return fetch(`${BASE}/runs/${runId}/validate`, {
    method: "POST",
    headers: authHeaders(token, false),
  }).then((r) => handle<CustomFieldError[]>(r));
}

// Config-derived name-suffix defaults + source label for the start screen.
export function fetchCompareDefaults(token: string): Promise<GabCompareDefaults> {
  return fetch(`${BASE}/compare-defaults`, { headers: authHeaders(token, false) }).then((r) =>
    handle<GabCompareDefaults>(r)
  );
}

// `overrides` carries the operator's per-run name-suffix choices; omit to use the server's config
// defaults. An empty-string suffix is sent as-is (a real "no suffix" choice, e.g. a Prod source).
export function startCompare(
  token: string,
  productGuid: string,
  overrides?: { sourceNameSuffix: string; targetNameSuffix: string; newOnly: boolean }
): Promise<GabSyncRunSummary> {
  return fetch(`${BASE}/products/${productGuid}/compare`, {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(overrides ?? {}),
  }).then((r) => handle<GabSyncRunSummary>(r));
}

export function fetchRun(token: string, runId: string): Promise<GabSyncRunDetail> {
  return fetch(`${BASE}/runs/${runId}`, { headers: authHeaders(token, false) }).then((r) =>
    handle<GabSyncRunDetail>(r)
  );
}

export function submitResolutions(
  token: string,
  runId: string,
  resolutions: RowResolution[]
): Promise<GabSyncRunSummary> {
  return fetch(`${BASE}/runs/${runId}/resolutions`, {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(resolutions),
  }).then((r) => handle<GabSyncRunSummary>(r));
}

export function cancelRun(token: string, runId: string): Promise<GabSyncRunSummary> {
  return fetch(`${BASE}/runs/${runId}/cancel`, {
    method: "POST",
    headers: authHeaders(token, false),
  }).then((r) => handle<GabSyncRunSummary>(r));
}
